import React from "react";
import api from "@/lib/api";
import { X, CheckCircle } from "@phosphor-icons/react";

export function DemoForm({ onSuccess, compact, source = "book-a-demo", testidPrefix = "demo" }) {
  const [form, setForm] = React.useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/leads", { ...form, source });
      setDone(true);
      onSuccess && onSuccess();
    } catch (e2) {
      // Normalise any error shape (string / Pydantic list / object) so the
      // form always shows a readable message rather than "[object Object]".
      const d = e2?.response?.data?.detail;
      let msg = "Something went wrong. Please try again.";
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d) && d.length && d[0]?.msg) msg = d[0].msg;
      else if (e2?.message) msg = e2.message;
      setErr(msg);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid={`${testidPrefix}-success`}>
        <CheckCircle size={48} weight="fill" style={{ color: "#1F6B4A" }} className="mx-auto" />
        <h3 className="mt-4 text-xl font-bold">Demo requested successfully</h3>
        <p className="mt-1 text-sm text-[#555]">Our team will contact you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testidPrefix}-form`}>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#555] mb-1.5">Name</label>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid={`${testidPrefix}-name`} className="w-full border border-[#e5e5e5] rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B]" />
      </div>
      <div>
        <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#555] mb-1.5">Work Email</label>
        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid={`${testidPrefix}-email`} className="w-full border border-[#e5e5e5] rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B]" />
      </div>
      {!compact && (
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-[0.2em] text-[#555] mb-1.5">Which ERP / EPM do you run?</label>
          <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid={`${testidPrefix}-message`} className="w-full border border-[#e5e5e5] rounded-none px-4 py-3 text-sm focus:outline-none focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B]" />
        </div>
      )}
      {err && <div className="text-xs text-[#DC2626] font-semibold" data-testid={`${testidPrefix}-error`}>{err}</div>}
      <button type="submit" disabled={busy} data-testid={`${testidPrefix}-submit`}
        className="w-full bg-[#0B1E3F] text-white rounded-none px-6 py-3 text-sm font-semibold hover:bg-[#C8A24B] transition-colors disabled:opacity-50">
        {busy ? "Submitting…" : "Request a Demo"}
      </button>
      <p className="text-[11px] text-[#999] font-mono text-center">Confidential &amp; Private</p>
    </form>
  );
}

export default function DemoModal({ open, onClose, source = "book-a-demo" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md border border-[#111] max-h-[90vh] overflow-y-auto" data-testid="demo-modal">
        <div className="flex items-start justify-between p-6 border-b border-[#e5e5e5]">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#555]">
              {source === "tailored-quote" ? "Pricing" : "Get Started"}
            </div>
            <h3 className="text-2xl font-bold tracking-tight mt-1">
              {source === "tailored-quote" ? "Get a tailored quote" : "See Vaku in action"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="demo-modal-close"><X size={20} /></button>
        </div>
        <div className="p-6">
          <DemoForm testidPrefix="modal-demo" source={source} onSuccess={() => {}} />
        </div>
      </div>
    </div>
  );
}
